/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  LOCATIONAL INTEL — where the officers in a holder's scope last reported a location. [LOCATION-INTEL]
 *
 *  ⚠️ A MODULE WITH NO IMPORTS, `officerTrail.constant.ts`' rule: the band vocabulary and its edges are
 *  read by the API (which decides a band) and by the web (which words the legend), and nothing here may
 *  drag zod or luxon into either.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * How recently an officer last REPORTED a location — never whether they are on duty. Duty Mitra sends
 * only while a duty is in progress (its consent box says so) and the mobile app sends whenever it is
 * opened, so an `OLDER` point is usually an officer who is simply off duty, not one anybody has lost.
 *
 * ⚠️ DECIDED ON THE SERVER, ON THE SERVER'S CLOCK (`recorded_at`), and sent beside the raw instant. A
 * device with a wrong clock cannot paint the city green, and no client computes a band.
 */
export const LOCATION_RECENCY_BANDS = ['LAST_HOUR', 'LAST_SIX_HOURS', 'OLDER'] as const;
export type LocationRecencyBand = (typeof LOCATION_RECENCY_BANDS)[number];

/**
 * The two edges, in hours and INCLUSIVE: a report exactly an hour old is still `LAST_HOUR`. One table
 * read by the API's band and the web's legend words, so the colour and the sentence beside it cannot
 * disagree about where an edge is.
 */
export const LOCATION_RECENCY_EDGE_HOURS = {
  LAST_HOUR: 1,
  LAST_SIX_HOURS: 6,
} as const satisfies Record<Exclude<LocationRecencyBand, 'OLDER'>, number>;

/**
 * One page of points. `GET /location-intel/points` grows with the OFFICER count, so it is paginated
 * (CLAUDE.md §7: no exception for that), and the page is large because a map needs every point: the
 * client pages through. A thousand points is about 90 KB of JSON.
 */
export const LOCATION_INTEL_PAGE_MAX = 1_000;
